import { DEFAULT_PERIOD_DAYS, isPeriodValid } from 'components/offerPortal/contingencyPeriods';
import {
  documentsForFinancing,
  isDocumentForFinancing,
} from 'components/offerPortal/financingDocument';
import {
  isAllowedCloseDate,
  type ListingDetails,
  showsMlsOfficeId,
} from 'components/offerPortal/listingDetails';
import {
  type OfferDocumentPurpose,
  type OfferDocumentRef,
  type OfferDraft,
  type SignatureCoordination,
  updateOfferDraft,
} from 'components/offerPortal/offerDraft';
import {
  buildSubmitRequest,
  type IntakeViolation,
  isLoanFinancing,
  MAX_AGENT_FIELD_LENGTH,
  MAX_CENTS,
  submitOffer,
  type SubmitOfferResponse,
} from 'components/offerPortal/offerSubmission';
import { isValidEmailAddress } from 'components/shared/emailValidation';
import { e164UsPhone } from 'components/shared/phone';
import { assign, fromPromise, setup } from 'xstate';

/**
 * The Offer Portal wizard flow as a state machine, following the
 * BuyDirectCheckout one: each step is a state, the answers live in context,
 * and Continue is a guarded transition — a step change is impossible while
 * its answers are invalid. Components render the current state and send
 * setter events; none of them owns flow control.
 */
export enum WizardStep {
  OfferTerms = 'OFFER_TERMS',
  Details = 'DETAILS',
  DataForDocuments = 'DATA_FOR_DOCUMENTS',
  SubmittingAs = 'SUBMITTING_AS',
  Documents = 'DOCUMENTS',
  Review = 'REVIEW',
  Submitting = 'SUBMITTING',
  Submitted = 'SUBMITTED',
}

/** The steps in visit order — the "Step N of M" label indexes into this. */
export const WIZARD_STEP_ORDER: readonly WizardStep[] = [
  WizardStep.OfferTerms,
  WizardStep.Details,
  WizardStep.DataForDocuments,
  WizardStep.SubmittingAs,
  WizardStep.Documents,
  WizardStep.Review,
];

export interface OfferPortalWizardContext {
  slug: string;
  listing: ListingDetails;
  // The draft fields, in the draft's canonical units (integer cents / bps).
  priceCents: number | null;
  sellerConcessionCents: number | null;
  buyerAgentCompensationBps: number | null;
  earnestDepositCents: number | null;
  inspectionPeriodDays: number | null;
  appraisalPeriodDays: number | null;
  financingPeriodDays: number | null;
  downPaymentCents: number | null;
  lenderType: string | null;
  financingType: string | null;
  closeDate: string | null;
  contingentOnSale: boolean | null;
  contingentHomeStatus: string | null;
  buyerFullName: string;
  buyerEmail: string;
  agentFirstName: string;
  agentLastName: string;
  agentEmail: string;
  agentPhone: string;
  agentLicenseNumber: string;
  /** Whether the submitting agent is an MLS member. */
  agentType: 'mls' | 'non_mls';
  agentMlsId: string;
  agentMlsOfficeId: string;
  signatureCoordination: SignatureCoordination;
  /** Uploaded document references; the file bytes live in S3, never here. */
  documents: OfferDocumentRef[];
  /** Server-minted id grouping this session's uploads under one S3 prefix. */
  documentsSubmissionId: string | null;
  /** Uploads still in flight — Continue holds until every one settles. */
  documentUploadsInFlight: number;
  /** Whether the current step failed its last Continue. */
  showError: boolean;
  /** Intake's per-field rejections, keyed by mutation input field. */
  fieldViolations: Record<string, string>;
  /** Why the last submission attempt did not create the offer. */
  submitError: string | null;
  /** The created offer's id — set only after the backend accepted it. */
  offerId: number | null;
  /** The created offer's UUID and its home's address UUID, when the backend returned them. */
  listingOfferUuid: string | null;
  addressUuid: string | null;
}

export type OfferPortalWizardEvent =
  | { type: 'HYDRATE'; draft: OfferDraft }
  | { type: 'SET_PRICE_CENTS'; value: number | null }
  | { type: 'SET_SELLER_CONCESSION_CENTS'; value: number | null }
  | { type: 'SET_BUYER_AGENT_COMPENSATION_BPS'; value: number | null }
  | { type: 'SET_EARNEST_DEPOSIT_CENTS'; value: number | null }
  | { type: 'SET_INSPECTION_PERIOD_DAYS'; value: number | null }
  | { type: 'SET_FINANCING_APPRAISAL_PERIOD_DAYS'; value: number | null }
  | { type: 'SET_DOWN_PAYMENT_CENTS'; value: number | null }
  | { type: 'SET_LENDER_TYPE'; value: string }
  | { type: 'SET_FINANCING_TYPE'; value: string }
  | { type: 'SET_CLOSE_DATE'; value: string }
  | { type: 'CLEAR_CLOSE_DATE' }
  | { type: 'SET_CONTINGENT_ON_SALE'; value: boolean }
  | { type: 'SET_CONTINGENT_HOME_STATUS'; value: string }
  | { type: 'SET_BUYER_FULL_NAME'; value: string }
  | { type: 'SET_BUYER_EMAIL'; value: string }
  | { type: 'SET_AGENT_FIRST_NAME'; value: string }
  | { type: 'SET_AGENT_LAST_NAME'; value: string }
  | { type: 'SET_AGENT_EMAIL'; value: string }
  | { type: 'SET_AGENT_PHONE'; value: string }
  | { type: 'SET_AGENT_LICENSE_NUMBER'; value: string }
  | { type: 'SET_AGENT_TYPE'; value: 'mls' | 'non_mls' }
  | { type: 'SET_AGENT_MLS_ID'; value: string }
  | { type: 'SET_AGENT_MLS_OFFICE_ID'; value: string }
  | { type: 'SET_SIGNATURE_COORDINATION'; value: SignatureCoordination }
  | { type: 'ADD_DOCUMENT'; document: OfferDocumentRef }
  | { type: 'REMOVE_DOCUMENT'; documentId: string }
  | { type: 'SET_DOCUMENTS_SUBMISSION_ID'; value: string }
  | { type: 'UPLOAD_STARTED' }
  | { type: 'UPLOAD_SETTLED' }
  | { type: 'CONTINUE' }
  | { type: 'BACK' }
  | { type: 'EDIT'; step: WizardStep }
  | { type: 'SUBMIT' };

/** The offer price must exist, be positive, and respect the listing's cap. */
export const isPriceValid = (context: OfferPortalWizardContext): boolean => {
  const cap = context.listing.maxAllowedOfferPriceCents;
  return (
    context.priceCents != null &&
    context.priceCents > 0 &&
    context.priceCents <= MAX_CENTS &&
    (cap == null || context.priceCents <= cap)
  );
};

/** Intake rejects money above the offer price or the column max. */
export const isMoneyAskValid = (value: number | null, priceCents: number | null): boolean =>
  value == null ||
  (value >= 0 && value <= MAX_CENTS && (priceCents == null || value <= priceCents));

/** Intake requires the earnest deposit; an explicit $0 means waived. */
export const isEarnestDepositValid = (context: OfferPortalWizardContext): boolean =>
  context.earnestDepositCents != null &&
  isMoneyAskValid(context.earnestDepositCents, context.priceCents);

const MAX_BUYER_AGENT_COMPENSATION_BPS = 400;

export const isCompensationValid = (context: OfferPortalWizardContext): boolean =>
  context.buyerAgentCompensationBps != null &&
  context.buyerAgentCompensationBps >= 0 &&
  context.buyerAgentCompensationBps <= MAX_BUYER_AGENT_COMPENSATION_BPS;

const isOfferTermsValid = (context: OfferPortalWizardContext): boolean =>
  isPriceValid(context) &&
  isEarnestDepositValid(context) &&
  context.sellerConcessionCents != null &&
  isMoneyAskValid(context.sellerConcessionCents, context.priceCents) &&
  isCompensationValid(context);

const arePeriodsValid = (context: OfferPortalWizardContext): boolean =>
  isPeriodValid(context.inspectionPeriodDays, 'inspectionPeriodDays') &&
  isPeriodValid(context.appraisalPeriodDays, 'appraisalPeriodDays') &&
  isPeriodValid(context.financingPeriodDays, 'financingPeriodDays') &&
  context.financingPeriodDays === context.appraisalPeriodDays;

/** Loan financing requires the down payment and lender; intake rejects both otherwise. */
const areLoanFieldsValid = (context: OfferPortalWizardContext): boolean =>
  !isLoanFinancing(context.financingType) ||
  (context.downPaymentCents != null &&
    isMoneyAskValid(context.downPaymentCents, context.priceCents) &&
    context.lenderType != null);

/**
 * The close date must be one the home supports, and an FHA offer cannot close
 * before the home's FHA eligibility date.
 */
export const isCloseDateValid = (context: OfferPortalWizardContext): boolean => {
  if (context.closeDate == null) {
    return false;
  }
  const today = new Date();
  if (!isAllowedCloseDate(context.listing, new Date(`${context.closeDate}T00:00:00`), today)) {
    return false;
  }
  const fhaDate = context.listing.fhaEligibleDate;
  if (context.financingType === 'fha' && fhaDate != null && context.closeDate < fhaDate) {
    return false;
  }
  return true;
};

const isDetailsValid = (context: OfferPortalWizardContext): boolean =>
  context.financingType != null &&
  isCloseDateValid(context) &&
  arePeriodsValid(context) &&
  areLoanFieldsValid(context) &&
  context.contingentOnSale != null &&
  (!context.contingentOnSale || context.contingentHomeStatus != null);

/** A contract-worthy name: non-blank and containing at least one letter. */
export const isFullNameValid = (name: string): boolean => /\p{L}/u.test(name.trim());

/** Blank is allowed. A value must be an email address. */
export const isBuyerEmailValid = (email: string): boolean => {
  const trimmed = email.trim();
  return trimmed === '' || isValidEmailAddress(trimmed);
};

const isBuyerValid = (context: OfferPortalWizardContext): boolean =>
  isFullNameValid(context.buyerFullName) && isBuyerEmailValid(context.buyerEmail);

/** Only numbers submission can normalize to E.164 may pass the step. */
export const isAgentPhoneValid = (phone: string): boolean => e164UsPhone(phone) != null;

const fitsAgentField = (value: string): boolean => value.trim().length <= MAX_AGENT_FIELD_LENGTH;

/** A contract-worthy agent name that also fits the submission cap. */
export const isAgentNameValid = (name: string): boolean =>
  isFullNameValid(name) && fitsAgentField(name);

/** The pair also submits joined as contactName, which shares the cap. */
export const isAgentNamePairValid = (first: string, last: string): boolean =>
  `${first.trim()} ${last.trim()}`.length <= MAX_AGENT_FIELD_LENGTH;

/** A shown identifier: non-blank and within the submission cap. */
export const isAgentIdentifierValid = (value: string): boolean =>
  value.trim() !== '' && fitsAgentField(value);

export const isAgentLicenseNumberValid = (value: string): boolean =>
  value.trim() === '' || isAgentIdentifierValid(value);

const areAgentIdentifiersValid = (context: OfferPortalWizardContext): boolean => {
  if (context.agentType === 'mls') {
    return (
      isAgentIdentifierValid(context.agentMlsId) &&
      (!showsMlsOfficeId(context.listing, context.agentType) ||
        isAgentIdentifierValid(context.agentMlsOfficeId))
    );
  }
  return isAgentLicenseNumberValid(context.agentLicenseNumber);
};

const isAgentValid = (context: OfferPortalWizardContext): boolean =>
  isAgentNameValid(context.agentFirstName) &&
  isAgentNameValid(context.agentLastName) &&
  isAgentNamePairValid(context.agentFirstName, context.agentLastName) &&
  isValidEmailAddress(context.agentEmail.trim()) &&
  isAgentPhoneValid(context.agentPhone) &&
  areAgentIdentifiersValid(context);

/**
 * The three contract documents the Listing Partner cannot proceed without.
 * Intake accepts an offer with none of them, but the checklist it generates
 * declares them required, so an offer without them lands as work nobody can
 * do — the step asks for them up front instead.
 */
export const REQUIRED_DOCUMENT_PURPOSES = [
  'PURCHASE_AGREEMENT',
  'ABA_DISCLOSURE',
  'OPENDOOR_ADDENDUM',
] as const satisfies readonly OfferDocumentPurpose[];

export const documentFor = (
  context: OfferPortalWizardContext,
  purpose: OfferDocumentPurpose,
): OfferDocumentRef | undefined =>
  context.documents.find((document) => document.purpose === purpose);

/** Every required slot filled, and nothing still uploading. */
const areDocumentsSettled = (context: OfferPortalWizardContext): boolean =>
  context.documentUploadsInFlight === 0 &&
  REQUIRED_DOCUMENT_PURPOSES.every((purpose) => documentFor(context, purpose) != null);

/**
 * Whether a step's answers are complete, so the shell can disable Continue
 * rather than let a click bounce off the guard. Same predicates the machine's
 * transitions use, so the button and the guard can never disagree.
 */
export const isStepComplete = (step: WizardStep, context: OfferPortalWizardContext): boolean => {
  switch (step) {
    case WizardStep.OfferTerms:
      return isOfferTermsValid(context);
    case WizardStep.Details:
      return isDetailsValid(context);
    case WizardStep.DataForDocuments:
      return isBuyerValid(context);
    case WizardStep.SubmittingAs:
      return isAgentValid(context);
    case WizardStep.Documents:
      return areDocumentsSettled(context);
    default:
      return true;
  }
};

const draftFromContext = (context: OfferPortalWizardContext): OfferDraft => ({
  slug: context.slug,
  priceCents: context.priceCents,
  sellerConcessionCents: context.sellerConcessionCents,
  buyerAgentCompensationBps: context.buyerAgentCompensationBps,
  earnestDepositCents: context.earnestDepositCents,
  inspectionPeriodDays: context.inspectionPeriodDays,
  appraisalPeriodDays: context.appraisalPeriodDays,
  financingPeriodDays: context.financingPeriodDays,
  // Switching to non-loan financing drops the loan-only answers — intake
  // rejects them when they ride along on a cash or unknown-financing offer.
  downPaymentCents: isLoanFinancing(context.financingType) ? context.downPaymentCents : null,
  lenderType: isLoanFinancing(context.financingType) ? context.lenderType : null,
  financingType: context.financingType,
  closeDate: context.closeDate,
  contingentOnSale: context.contingentOnSale,
  contingentHomeStatus: context.contingentOnSale ? context.contingentHomeStatus : null,
  buyerFullName: context.buyerFullName.trim() || null,
  buyerEmail: context.buyerEmail.trim() || null,
  agentFirstName: context.agentFirstName.trim() || null,
  agentLastName: context.agentLastName.trim() || null,
  agentEmail: context.agentEmail.trim() || null,
  agentPhone: context.agentPhone.trim() || null,
  agentLicenseNumber: context.agentLicenseNumber.trim() || null,
  agentType: context.agentType,
  agentMlsId: context.agentMlsId.trim() || null,
  agentMlsOfficeId: context.agentMlsOfficeId.trim() || null,
  signatureCoordination: context.signatureCoordination,
  documents: context.documents,
  documentsSubmissionId: context.documentsSubmissionId,
});

/** The map without those fields' violations — editing the answer retires them. */
const withoutViolation = (
  violations: Record<string, string>,
  ...fields: string[]
): Record<string, string> => {
  if (!fields.some((field) => field in violations)) {
    return violations;
  }
  return Object.fromEntries(
    Object.entries(violations).filter(([field]) => !fields.includes(field)),
  );
};

/** A guarded Continue: advance when valid, otherwise surface the errors. */
const continueTo = (target: WizardStep) => [
  {
    guard: `${target}Reachable` as
      | 'DETAILSReachable'
      | 'DATA_FOR_DOCUMENTSReachable'
      | 'SUBMITTING_ASReachable'
      | 'DOCUMENTSReachable'
      | 'REVIEWReachable',
    target,
    actions: ['persistDraft', 'clearError'] as const,
  },
  { actions: 'showError' as const },
];

const submitOfferActor = fromPromise<SubmitOfferResponse, OfferPortalWizardContext>(
  async ({ input }) => {
    const request = buildSubmitRequest(input.listing, draftFromContext(input));
    // Every step was validated on the way to Review, so a gap here means the
    // context was tampered with — refuse rather than submit altered terms.
    if (!request) {
      return { kind: 'failed' };
    }
    return submitOffer(request);
  },
);

export const submitErrorMessage = (result: SubmitOfferResponse): string => {
  switch (result.kind) {
    case 'intakeRejected':
      return result.violations.some((violation) => violation.reason === 'DUPLICATE')
        ? 'You already have an active offer on this home.'
        : result.message;
    case 'rateLimited':
      // Zeus's own field limit resets within a minute; the Rails per-IP
      // bucket answers with an hour-scale retry hint.
      return (result.retryAfterSeconds ?? 0) >= 3600
        ? 'Too many offers from this connection. Wait about an hour and try again.'
        : 'Too many offers from this connection — wait a minute and try again.';
    default:
      return 'Something went wrong submitting the offer. Try again.';
  }
};

/** Which wizard step edits each mutation input field intake can reject. */
const VIOLATION_STEP: Record<string, WizardStep> = {
  priceCents: WizardStep.OfferTerms,
  concessionsFromSellerCents: WizardStep.OfferTerms,
  brokerCommissionRate: WizardStep.OfferTerms,
  earnestDepositCents: WizardStep.OfferTerms,
  financingType: WizardStep.Details,
  closeOfEscrow: WizardStep.Details,
  inspectionPeriodDays: WizardStep.Details,
  appraisalPeriodDays: WizardStep.Details,
  financingPeriodDays: WizardStep.Details,
  downPaymentCents: WizardStep.Details,
  lenderType: WizardStep.Details,
  buyerHomeSaleStatus: WizardStep.Details,
  buyerName: WizardStep.DataForDocuments,
  buyerEmail: WizardStep.DataForDocuments,
  contactName: WizardStep.SubmittingAs,
  contactEmail: WizardStep.SubmittingAs,
  contactPhone: WizardStep.SubmittingAs,
  agentFirstName: WizardStep.SubmittingAs,
  agentLastName: WizardStep.SubmittingAs,
  agentType: WizardStep.SubmittingAs,
  agentMlsId: WizardStep.SubmittingAs,
  agentMlsOfficeId: WizardStep.SubmittingAs,
  signatureCoordination: WizardStep.SubmittingAs,
  agentLicenseNumber: WizardStep.SubmittingAs,
  documents: WizardStep.Documents,
};

/**
 * The earliest wizard step owning a rejected field — where the wizard jumps
 * so the agent lands on the first thing to fix. Undefined when no violation
 * maps to a step (a duplicate offer, a whole-contact rejection): those read
 * as a banner on the review step instead.
 */
// A duplicate offer is not fixable by editing the flagged field, so it stays
// on the review banner rather than routing a jump.
const isStepViolation = (
  violation: IntakeViolation,
): violation is IntakeViolation & { field: string } =>
  violation.reason !== 'DUPLICATE' &&
  violation.field != null &&
  VIOLATION_STEP[violation.field] != null;

const firstViolationStep = (violations: IntakeViolation[]): WizardStep | undefined => {
  const steps = violations
    .filter(isStepViolation)
    .map((violation) => VIOLATION_STEP[violation.field]);
  return WIZARD_STEP_ORDER.find((step) => steps.includes(step));
};

const fieldViolationsFrom = (violations: IntakeViolation[]): Record<string, string> =>
  Object.fromEntries(
    violations.filter(isStepViolation).map((violation) => [violation.field, violation.message]),
  );

/** An intake rejection whose fixes live on `step` — used to route the jump. */
const isRejectedAt = (output: SubmitOfferResponse, step: WizardStep): boolean =>
  output.kind === 'intakeRejected' && firstViolationStep(output.violations) === step;

const violationsFromOutput = (output: SubmitOfferResponse): Record<string, string> =>
  output.kind === 'intakeRejected' ? fieldViolationsFrom(output.violations) : {};

export const offerPortalWizardMachine = setup({
  types: {
    context: {} as OfferPortalWizardContext,
    events: {} as OfferPortalWizardEvent,
    input: {} as { slug: string; listing: ListingDetails },
  },
  actors: {
    submitOffer: submitOfferActor,
  },
  guards: {
    DETAILSReachable: ({ context }) => isOfferTermsValid(context),
    DATA_FOR_DOCUMENTSReachable: ({ context }) => isDetailsValid(context),
    SUBMITTING_ASReachable: ({ context }) => isBuyerValid(context),
    DOCUMENTSReachable: ({ context }) => isAgentValid(context),
    REVIEWReachable: ({ context }) => areDocumentsSettled(context),
    // Leaving backwards only waits for in-flight uploads: a missing required
    // document must not trap the agent on the step.
    uploadsIdle: ({ context }) => context.documentUploadsInFlight === 0,
  },
  actions: {
    showError: assign({ showError: true }),
    clearError: assign({ showError: false }),
    persistDraft: ({ context }) => {
      updateOfferDraft(context.slug, draftFromContext(context));
    },
  },
}).createMachine({
  id: 'offer-portal-wizard',
  context: ({ input }) => ({
    slug: input.slug,
    listing: input.listing,
    priceCents: null,
    sellerConcessionCents: 0,
    buyerAgentCompensationBps: 250,
    earnestDepositCents: null,
    inspectionPeriodDays: DEFAULT_PERIOD_DAYS.inspection,
    appraisalPeriodDays: DEFAULT_PERIOD_DAYS.financingAppraisal,
    financingPeriodDays: DEFAULT_PERIOD_DAYS.financingAppraisal,
    downPaymentCents: null,
    lenderType: null,
    financingType: null,
    closeDate: null,
    contingentOnSale: false,
    contingentHomeStatus: null,
    buyerFullName: '',
    buyerEmail: '',
    agentFirstName: '',
    agentLastName: '',
    agentEmail: '',
    agentPhone: '',
    agentLicenseNumber: '',
    agentType: 'mls',
    agentMlsId: '',
    agentMlsOfficeId: '',
    signatureCoordination: 'buyer_agent',
    documents: [],
    documentsSubmissionId: null,
    documentUploadsInFlight: 0,
    showError: false,
    fieldViolations: {},
    submitError: null,
    offerId: null,
    listingOfferUuid: null,
    addressUuid: null,
  }),
  initial: WizardStep.OfferTerms,
  on: {
    // Sent once after mount: sessionStorage is client-only, so the draft
    // cannot be part of the initial context without a hydration mismatch.
    HYDRATE: {
      actions: assign(({ event }) => ({
        priceCents: event.draft.priceCents,
        sellerConcessionCents: event.draft.sellerConcessionCents ?? 0,
        buyerAgentCompensationBps: event.draft.buyerAgentCompensationBps ?? 250,
        earnestDepositCents: event.draft.earnestDepositCents,
        inspectionPeriodDays: event.draft.inspectionPeriodDays ?? DEFAULT_PERIOD_DAYS.inspection,
        appraisalPeriodDays:
          event.draft.financingType === 'cash'
            ? 0
            : (event.draft.appraisalPeriodDays ?? DEFAULT_PERIOD_DAYS.financingAppraisal),
        financingPeriodDays:
          event.draft.financingType === 'cash'
            ? 0
            : (event.draft.financingPeriodDays ?? DEFAULT_PERIOD_DAYS.financingAppraisal),
        downPaymentCents: event.draft.downPaymentCents,
        lenderType: event.draft.lenderType,
        financingType: event.draft.financingType,
        closeDate: event.draft.closeDate,
        contingentOnSale: event.draft.contingentOnSale ?? false,
        contingentHomeStatus: event.draft.contingentHomeStatus,
        buyerFullName: event.draft.buyerFullName ?? '',
        buyerEmail: event.draft.buyerEmail ?? '',
        agentFirstName: event.draft.agentFirstName ?? '',
        agentLastName: event.draft.agentLastName ?? '',
        agentEmail: event.draft.agentEmail ?? '',
        agentPhone: event.draft.agentPhone ?? '',
        agentLicenseNumber: event.draft.agentLicenseNumber ?? '',
        agentType: event.draft.agentType,
        agentMlsId: event.draft.agentMlsId ?? '',
        agentMlsOfficeId: event.draft.agentMlsOfficeId ?? '',
        signatureCoordination: event.draft.signatureCoordination,
        documents: documentsForFinancing(event.draft.documents, event.draft.financingType),
        documentsSubmissionId: event.draft.documentsSubmissionId,
      })),
    },
    SET_SELLER_CONCESSION_CENTS: {
      actions: assign(({ context, event }) => ({
        sellerConcessionCents: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'concessionsFromSellerCents'),
      })),
    },
    SET_BUYER_AGENT_COMPENSATION_BPS: {
      actions: assign(({ context, event }) => ({
        buyerAgentCompensationBps: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'brokerCommissionRate'),
      })),
    },
    SET_EARNEST_DEPOSIT_CENTS: {
      actions: assign(({ context, event }) => ({
        earnestDepositCents: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'earnestDepositCents'),
      })),
    },
    SET_INSPECTION_PERIOD_DAYS: {
      actions: assign(({ context, event }) => ({
        inspectionPeriodDays: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'inspectionPeriodDays'),
      })),
    },
    SET_FINANCING_APPRAISAL_PERIOD_DAYS: {
      guard: ({ context }) => context.financingType !== 'cash',
      actions: assign(({ context, event }) => ({
        appraisalPeriodDays: event.value,
        financingPeriodDays: event.value,
        fieldViolations: withoutViolation(
          withoutViolation(context.fieldViolations, 'appraisalPeriodDays'),
          'financingPeriodDays',
        ),
      })),
    },
    SET_DOWN_PAYMENT_CENTS: {
      actions: assign(({ context, event }) => ({
        downPaymentCents: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'downPaymentCents'),
      })),
    },
    SET_LENDER_TYPE: {
      actions: assign(({ context, event }) => ({
        lenderType: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'lenderType'),
      })),
    },
    // Entering a price or picking the financing answers the reddened
    // question, so these two also clear the step's error state.
    SET_PRICE_CENTS: {
      actions: assign(({ context, event }) => ({
        priceCents: event.value,
        showError: false,
        fieldViolations: withoutViolation(context.fieldViolations, 'priceCents'),
      })),
    },
    SET_FINANCING_TYPE: {
      actions: [
        assign(({ context, event }) => {
          const isCash = event.value === 'cash';
          const resetPeriods = isCash || context.financingType === 'cash';
          const periodDays = isCash ? 0 : DEFAULT_PERIOD_DAYS.financingAppraisal;
          const fieldViolations = withoutViolation(context.fieldViolations, 'financingType');
          const financingPeriodUpdates = resetPeriods
            ? {
                financingPeriodDays: periodDays,
                appraisalPeriodDays: periodDays,
                fieldViolations: withoutViolation(
                  fieldViolations,
                  'appraisalPeriodDays',
                  'financingPeriodDays',
                ),
              }
            : { fieldViolations };
          return {
            financingType: event.value,
            documents: documentsForFinancing(context.documents, event.value),
            showError: false,
            ...financingPeriodUpdates,
          };
        }),
        'persistDraft',
      ],
    },
    SET_CLOSE_DATE: {
      actions: assign(({ context, event }) => ({
        closeDate: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'closeOfEscrow'),
      })),
    },
    CLEAR_CLOSE_DATE: {
      actions: assign(({ context }) => ({
        closeDate: null,
        fieldViolations: withoutViolation(context.fieldViolations, 'closeOfEscrow'),
      })),
    },
    SET_CONTINGENT_ON_SALE: {
      actions: assign(({ context, event }) => ({
        contingentOnSale: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'buyerHomeSaleStatus'),
      })),
    },
    SET_CONTINGENT_HOME_STATUS: {
      actions: assign(({ context, event }) => ({
        contingentHomeStatus: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'buyerHomeSaleStatus'),
      })),
    },
    SET_BUYER_FULL_NAME: {
      actions: assign(({ context, event }) => ({
        buyerFullName: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'buyerName'),
      })),
    },
    SET_BUYER_EMAIL: {
      actions: assign(({ context, event }) => ({
        buyerEmail: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'buyerEmail'),
      })),
    },
    SET_AGENT_FIRST_NAME: {
      actions: assign(({ context, event }) => ({
        agentFirstName: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'contactName', 'agentFirstName'),
      })),
    },
    SET_AGENT_LAST_NAME: {
      actions: assign(({ context, event }) => ({
        agentLastName: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'contactName', 'agentLastName'),
      })),
    },
    SET_AGENT_EMAIL: {
      actions: assign(({ context, event }) => ({
        agentEmail: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'contactEmail'),
      })),
    },
    SET_AGENT_PHONE: {
      actions: assign(({ context, event }) => ({
        agentPhone: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'contactPhone'),
      })),
    },
    SET_AGENT_LICENSE_NUMBER: {
      actions: assign(({ context, event }) => ({
        agentLicenseNumber: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'agentLicenseNumber'),
      })),
    },
    // Switching type keeps whatever was typed: an agent who flips back finds
    // their identifiers still there, and the unused ones never submit.
    SET_AGENT_TYPE: {
      actions: assign(({ context, event }) => ({
        agentType: event.value,
        showError: false,
        fieldViolations: withoutViolation(context.fieldViolations, 'agentType'),
      })),
    },
    SET_AGENT_MLS_ID: {
      actions: assign(({ context, event }) => ({
        agentMlsId: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'agentMlsId'),
      })),
    },
    SET_AGENT_MLS_OFFICE_ID: {
      actions: assign(({ context, event }) => ({
        agentMlsOfficeId: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'agentMlsOfficeId'),
      })),
    },
    SET_SIGNATURE_COORDINATION: {
      actions: assign(({ context, event }) => ({
        signatureCoordination: event.value,
        fieldViolations: withoutViolation(context.fieldViolations, 'signatureCoordination'),
      })),
    },
    // Adding or removing a document persists the draft immediately: an upload
    // is too expensive to lose to a refresh before the next Continue.
    ADD_DOCUMENT: {
      guard: ({ context, event }) =>
        isDocumentForFinancing(event.document.purpose, context.financingType),
      actions: [
        assign(({ context, event }) => ({
          // A required slot holds one document: re-uploading replaces it.
          // Extras accumulate.
          documents:
            event.document.purpose === 'OTHER'
              ? [...context.documents, event.document]
              : [
                  ...context.documents.filter(
                    (document) => document.purpose !== event.document.purpose,
                  ),
                  event.document,
                ],
          fieldViolations: withoutViolation(context.fieldViolations, 'documents'),
        })),
        'persistDraft',
      ],
    },
    REMOVE_DOCUMENT: {
      actions: [
        assign(({ context, event }) => ({
          documents: context.documents.filter(
            (document) => document.documentId !== event.documentId,
          ),
          fieldViolations: withoutViolation(context.fieldViolations, 'documents'),
        })),
        'persistDraft',
      ],
    },
    SET_DOCUMENTS_SUBMISSION_ID: {
      actions: assign(({ event }) => ({ documentsSubmissionId: event.value })),
    },
    UPLOAD_STARTED: {
      actions: assign(({ context }) => ({
        documentUploadsInFlight: context.documentUploadsInFlight + 1,
        showError: false,
      })),
    },
    UPLOAD_SETTLED: {
      actions: assign(({ context }) => ({
        documentUploadsInFlight: Math.max(0, context.documentUploadsInFlight - 1),
      })),
    },
  },
  states: {
    [WizardStep.OfferTerms]: {
      on: {
        CONTINUE: continueTo(WizardStep.Details),
      },
    },
    [WizardStep.Details]: {
      on: {
        CONTINUE: continueTo(WizardStep.DataForDocuments),
        BACK: { target: WizardStep.OfferTerms, actions: 'clearError' },
      },
    },
    [WizardStep.DataForDocuments]: {
      on: {
        CONTINUE: continueTo(WizardStep.SubmittingAs),
        BACK: { target: WizardStep.Details, actions: 'clearError' },
      },
    },
    [WizardStep.SubmittingAs]: {
      on: {
        CONTINUE: continueTo(WizardStep.Documents),
        BACK: { target: WizardStep.DataForDocuments, actions: 'clearError' },
      },
    },
    [WizardStep.Documents]: {
      on: {
        CONTINUE: continueTo(WizardStep.Review),
        // Leaving the step would unmount the upload rows, so BACK holds until
        // every in-flight upload settles.
        BACK: [
          {
            guard: 'uploadsIdle',
            target: WizardStep.SubmittingAs,
            actions: 'clearError' as const,
          },
          { actions: 'showError' as const },
        ],
      },
    },
    [WizardStep.Review]: {
      on: {
        SUBMIT: {
          target: WizardStep.Submitting,
          actions: assign({ submitError: null, fieldViolations: {} }),
        },
        BACK: { target: WizardStep.Documents, actions: 'clearError' },
        // Backwards jumps from the review's Edit links are always allowed —
        // every earlier step was already validated on the way here.
        EDIT: [
          {
            guard: ({ event }) => event.step === WizardStep.OfferTerms,
            target: WizardStep.OfferTerms,
          },
          {
            guard: ({ event }) => event.step === WizardStep.Details,
            target: WizardStep.Details,
          },
          {
            guard: ({ event }) => event.step === WizardStep.DataForDocuments,
            target: WizardStep.DataForDocuments,
          },
          {
            guard: ({ event }) => event.step === WizardStep.SubmittingAs,
            target: WizardStep.SubmittingAs,
          },
          {
            guard: ({ event }) => event.step === WizardStep.Documents,
            target: WizardStep.Documents,
          },
        ],
      },
    },
    // No BACK/EDIT/setter transitions here: while the offer is in flight or
    // created, editing terms the offer does not have is impossible by
    // construction.
    [WizardStep.Submitting]: {
      invoke: {
        src: 'submitOffer',
        input: ({ context }) => context,
        onDone: [
          {
            guard: ({ event }) => event.output.kind === 'created',
            target: WizardStep.Submitted,
            actions: assign(({ event }) =>
              event.output.kind === 'created'
                ? {
                    offerId: event.output.offerId,
                    listingOfferUuid: event.output.listingOfferUuid,
                    addressUuid: event.output.addressUuid,
                  }
                : { offerId: null, listingOfferUuid: null, addressUuid: null },
            ),
          },
          // Typed intake rejections land the agent on the first step that
          // owns a rejected field, with the message under that field.
          {
            guard: ({ event }) => isRejectedAt(event.output, WizardStep.OfferTerms),
            target: WizardStep.OfferTerms,
            actions: assign(({ event }) => ({
              fieldViolations: violationsFromOutput(event.output),
              showError: true,
            })),
          },
          {
            guard: ({ event }) => isRejectedAt(event.output, WizardStep.Details),
            target: WizardStep.Details,
            actions: assign(({ event }) => ({
              fieldViolations: violationsFromOutput(event.output),
              showError: true,
            })),
          },
          {
            guard: ({ event }) => isRejectedAt(event.output, WizardStep.DataForDocuments),
            target: WizardStep.DataForDocuments,
            actions: assign(({ event }) => ({
              fieldViolations: violationsFromOutput(event.output),
              showError: true,
            })),
          },
          {
            guard: ({ event }) => isRejectedAt(event.output, WizardStep.SubmittingAs),
            target: WizardStep.SubmittingAs,
            actions: assign(({ event }) => ({
              fieldViolations: violationsFromOutput(event.output),
              showError: true,
            })),
          },
          {
            guard: ({ event }) => isRejectedAt(event.output, WizardStep.Documents),
            target: WizardStep.Documents,
            actions: assign(({ event }) => ({
              fieldViolations: violationsFromOutput(event.output),
              showError: true,
            })),
          },
          {
            target: WizardStep.Review,
            actions: assign(({ event }) => ({ submitError: submitErrorMessage(event.output) })),
          },
        ],
        onError: {
          target: WizardStep.Review,
          actions: assign({
            submitError: 'Something went wrong submitting the offer. Try again.',
          }),
        },
      },
    },
    // Terminal for the form: the component navigates to the confirmation
    // page from here, and a retry can only be that navigation — never
    // another POST.
    [WizardStep.Submitted]: {},
  },
});
